/**
 * Who from the company sits in a meeting — the `booking_host` reads and
 * writes.
 *
 * UNGUARDED like the rest of `@repo/db/queries`; `@repo/core` asks first. The
 * writes belong INSIDE the transaction that claims or moves the hour, after
 * `lockOfferingCalendar` and `freeMeetingHosts` (`./meeting-hosts.ts`), so the
 * set written is the set that was free at COMMIT.
 */
import { and, asc, eq, gt, inArray, lt, sql } from 'drizzle-orm'

import type { Queryable } from '../client.ts'
import { err, isErr, ok, type Result } from '../result.ts'
import { user } from '../schema/auth.ts'
import { bookingHost } from '../schema/booking-hosts.ts'
import { type Booking, booking } from '../schema/bookings.ts'
import { type DbError, dbError } from './errors.ts'
import type { MeetingHost } from './meeting-hosts.ts'

/**
 * Put people on a meeting. Already-present rows are left alone, so a retried
 * claim is one attendance, not an error.
 */
export async function addBookingHosts(
  db: Queryable,
  bookingId: string,
  userIds: readonly string[],
): Promise<Result<number, DbError>> {
  if (userIds.length === 0) return ok(0)
  try {
    const rows = await db
      .insert(bookingHost)
      .values([...new Set(userIds)].map((userId) => ({ bookingId, userId })))
      .onConflictDoNothing()
      .returning({ userId: bookingHost.userId })
    return ok(rows.length)
  } catch (cause) {
    return err(dbError('Failed to put hosts on a meeting', cause))
  }
}

/**
 * The meeting's attendees become exactly these people — what a reschedule
 * does, since whoever is free at the new hour is who sits in it.
 *
 * Two statements. Run it inside the transaction that moves the meeting, so
 * the old set and the new one land together or not at all.
 */
export async function replaceBookingHosts(
  db: Queryable,
  bookingId: string,
  userIds: readonly string[],
): Promise<Result<number, DbError>> {
  try {
    await db.delete(bookingHost).where(eq(bookingHost.bookingId, bookingId))
  } catch (cause) {
    return err(dbError('Failed to clear the hosts of a meeting', cause))
  }
  return await addBookingHosts(db, bookingId, userIds)
}

/** Take one person off a meeting. `false` when they were not on it. */
export async function removeBookingHost(
  db: Queryable,
  bookingId: string,
  userId: string,
): Promise<Result<boolean, DbError>> {
  try {
    const rows = await db
      .delete(bookingHost)
      .where(and(eq(bookingHost.bookingId, bookingId), eq(bookingHost.userId, userId)))
      .returning({ userId: bookingHost.userId })
    return ok(rows.length > 0)
  } catch (cause) {
    return err(dbError('Failed to take a host off a meeting', cause))
  }
}

/**
 * Who sits in one meeting, by name. Empty for a meeting nobody was put on, or
 * whose only people have since closed or lost their accounts.
 */
export async function listBookingHosts(
  db: Queryable,
  bookingId: string,
): Promise<Result<MeetingHost[], DbError>> {
  const many = await listBookingHostsFor(db, [bookingId])
  if (isErr(many)) return many
  return ok(many.value.get(bookingId) ?? [])
}

/**
 * The same, for a page of meetings at once — keyed by booking id.
 *
 * LIVE accounts only. A closed account is anonymised and banned rather than
 * deleted (`closeUserAccount`), and a blocked Admin keeps their rows too, so
 * their seat survives on record. Read as an attendee, it would let the last
 * real host leave a meeting because a tombstone „stays" on it, and would put
 * `deleted-…@flou.invalid` in the sentence that says who. Nobody is told
 * anything through such an account either, so no reader wants it back.
 */
export async function listBookingHostsFor(
  db: Queryable,
  bookingIds: readonly string[],
): Promise<Result<Map<string, MeetingHost[]>, DbError>> {
  const byBooking = new Map<string, MeetingHost[]>()
  if (bookingIds.length === 0) return ok(byBooking)
  try {
    const rows = await db
      .select({
        bookingId: bookingHost.bookingId,
        id: user.id,
        name: user.name,
        email: user.email,
      })
      .from(bookingHost)
      .innerJoin(user, eq(user.id, bookingHost.userId))
      .where(and(inArray(bookingHost.bookingId, [...new Set(bookingIds)]), eq(user.banned, false)))
      .orderBy(asc(user.name), asc(user.id))
    for (const { bookingId, ...host } of rows) {
      const list = byBooking.get(bookingId) ?? []
      list.push(host)
      byBooking.set(bookingId, list)
    }
    return ok(byBooking)
  } catch (cause) {
    return err(dbError('Failed to read the hosts of meetings', cause))
  }
}

/** A live meeting one person sits in. */
export interface HostedBooking {
  readonly bookingId: string
  readonly offeringId: string
  readonly title: string | null
  readonly status: Booking['status']
  readonly startsAt: Date
  readonly endsAt: Date
}

/**
 * The live meetings this person sits in that overlap `[from, to)` — on the
 * BLOCKED window, as the busy rule reads them.
 *
 * What a host's own diary write needs: an entry over a meeting they attend
 * takes them off it if somebody else stays, and is refused if they are the
 * last one there. CANCELLED and NO_SHOW are skipped, exactly as everywhere
 * else in the busy rule.
 */
export async function listHostedBookings(
  db: Queryable,
  input: { readonly userId: string; readonly from: Date; readonly to: Date },
): Promise<Result<HostedBooking[], DbError>> {
  try {
    const rows = await db
      .select({
        bookingId: booking.id,
        offeringId: booking.offeringId,
        title: booking.title,
        status: booking.status,
        startsAt: booking.startsAt,
        endsAt: booking.endsAt,
      })
      .from(bookingHost)
      .innerJoin(booking, eq(booking.id, bookingHost.bookingId))
      .where(
        and(
          eq(bookingHost.userId, input.userId),
          lt(booking.blockedFrom, input.to),
          gt(booking.blockedTo, input.from),
          sql`${booking.status} NOT IN ('CANCELLED', 'NO_SHOW')`,
        ),
      )
      .orderBy(asc(booking.startsAt))
    return ok(rows)
  } catch (cause) {
    return err(dbError("Failed to read a host's meetings", cause))
  }
}
